import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Clock } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// 4-step phase stepper. Two variants of the same thing:
//   • variant="desktop" → sticky second row below the main header on lg+
//     screens. Larger circles, roomier spacing, 13px labels, and a reserved
//     sub-label slot so Phase 4's "presto" sits in the same column alignment
//     without making the row any taller than the other steps.
//   • variant="mobile"  → compact row for narrow viewports; abbreviated
//     names + visible "presto" sub-label (short bar, saves vertical space).
// Shared logic: routes, soon-modal, URL-derived current phase.
const PHASES = [
  { key: "plan",    route: "/organizza" },
  { key: "checkin", route: "/checkin",  alwaysClickable: true },
  { key: "aboard",  route: "/abordo",   alwaysClickable: true },
  { key: "landing", route: null,        alwaysClickable: true, showSoonModal: true },
];

const PATH_TO_PHASE = {
  "/organizza": "plan",
  "/checkin":   "checkin",
  "/abordo":    "aboard",
  "/dashboard": "plan",
};

export default function PhaseIndicator({ variant = "mobile", currentOverride }) {
  const { t, phaseState } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [soonOpen, setSoonOpen] = useState(false);

  const current = currentOverride ?? PATH_TO_PHASE[location.pathname] ?? null;
  if (!current) return null;

  const unlocked = {
    plan: true,
    checkin: true,
    aboard: true,
    landing: !!phaseState?.landing?.unlocked,
  };

  const handleClick = (p) => {
    if (p.showSoonModal) { setSoonOpen(true); return; }
    if (p.route) navigate(p.route);
  };

  // ── Variant-specific sizing ─────────────────────────────────────────────
  const isDesktop   = variant === "desktop";
  const circlePx    = isDesktop ? "w-8 h-8"       : "w-7 h-7";                // 32 vs 28
  const circleFont  = isDesktop ? "text-[13px]"   : "text-[11px]";
  const nameFont    = isDesktop ? "text-[13px]"   : "text-[11px]";
  const subFont     = isDesktop ? "text-[11px]"   : "text-[10px]";
  // Vertical offset of the connecting line = circle vertical center
  // (desktop: py-1 top + 32/2 = 20px; mobile: py-1 top + 28/2 = 18px).
  const lineTop     = isDesktop ? "top-[20px]"    : "top-[18px]";
  const containerPy = isDesktop ? "py-2"          : "py-2";
  const gapY        = isDesktop ? "gap-1.5"       : "gap-1";
  // Container max-width + horizontal padding:
  //  • desktop: match the typical page content column width (max-w-4xl)
  //    so the stepper feels aligned with the content below it.
  //  • mobile: full width of the sticky header, modest side padding.
  const wrapper     = isDesktop
    ? "max-w-4xl mx-auto px-6 lg:px-8"
    : "max-w-5xl mx-auto px-4 sm:px-5";

  return (
    <>
      <div
        data-testid={`phase-indicator-${variant}`}
        className="w-full"
      >
        <div className={`relative ${wrapper} ${containerPy}`}>
          {/* Connecting line passing behind the circles. Starts at the
              horizontal center of the first column and ends at the center of
              the last column (12.5% / 87.5% for 4 equal-width columns). */}
          <div
            aria-hidden="true"
            className={`absolute ${lineTop} left-[12.5%] right-[12.5%] h-px bg-ink/15`}
          />

          <div className="relative flex items-start">
            {PHASES.map((p, i) => {
              const isCurrent  = p.key === current;
              const isSoon     = !!p.showSoonModal;
              const isClickable = p.showSoonModal || ((unlocked[p.key] || p.alwaysClickable) && !!p.route);

              const circleCls = isCurrent
                ? "bg-terracotta text-white border-2 border-terracotta shadow-[0_2px_8px_rgba(224,122,95,0.3)]"
                : isSoon
                  ? "bg-surface-card text-ink-soft/80 border border-ink/20"
                  : "bg-surface-card text-ink border border-ink/30 group-hover:border-terracotta/70 group-hover:text-terracotta";

              const nameCls = isCurrent
                ? "text-ink font-bold"
                : isSoon
                  ? "text-ink-soft/90 font-normal"
                  : "text-ink font-medium";

              const Wrapper = isClickable ? "button" : "div";
              const wrapperProps = isClickable
                ? { type: "button", onClick: () => handleClick(p), "data-testid": `phase-card-${p.key}` }
                : { "data-testid": `phase-card-${p.key}` };

              // Desktop stepper uses full phase names ("Organizza la
              // partenza", "In the USA"); mobile uses abbreviated names so
              // nothing wraps to 2 lines on a 390px viewport.
              const label = isDesktop
                ? t.phase[p.key]
                : t.phase[`short${p.key.charAt(0).toUpperCase() + p.key.slice(1)}`];

              return (
                <Wrapper
                  key={p.key}
                  {...wrapperProps}
                  className={`group flex-1 flex flex-col items-center ${gapY} min-h-[44px] py-1 px-2 text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta/40 rounded-lg`}
                >
                  <div
                    className={`relative z-[1] ${circlePx} rounded-full flex items-center justify-center font-display font-bold ${circleFont} transition-all ${circleCls}`}
                  >
                    {i + 1}
                  </div>
                  <div className={`${nameFont} leading-tight font-sans whitespace-nowrap ${nameCls}`}>
                    {label}
                  </div>
                  {/* Sub-label slot:
                      • Desktop: ALL 4 columns reserve a fixed-height row for
                        the "presto"/"soon" chip, so the overall row keeps
                        the same height whether the chip is present or not.
                        Only Phase 4 renders the chip; the other columns
                        render an invisible placeholder occupying the same
                        space.
                      • Mobile: only phase 4 renders the chip (short bar,
                        natural height is fine). */}
                  {isDesktop ? (
                    isSoon ? (
                      <div
                        data-testid={`phase-soon-sublabel-${p.key}`}
                        className={`${subFont} font-sans font-semibold tracking-wide uppercase text-ink-soft/80 leading-tight`}
                      >
                        {t.phase.soonBadge}
                      </div>
                    ) : (
                      <div
                        aria-hidden="true"
                        className={`${subFont} font-sans font-semibold tracking-wide uppercase leading-tight invisible`}
                      >
                        &nbsp;
                      </div>
                    )
                  ) : (
                    isSoon && (
                      <div
                        data-testid={`phase-soon-sublabel-${p.key}`}
                        className={`${subFont} font-sans font-semibold tracking-wide uppercase text-ink-soft/80 leading-tight`}
                      >
                        {t.phase.soonBadge}
                      </div>
                    )
                  )}
                </Wrapper>
              );
            })}
          </div>
        </div>
      </div>

      <Dialog open={soonOpen} onOpenChange={setSoonOpen}>
        <DialogContent data-testid="phase-soon-modal" className="max-w-md">
          <DialogHeader>
            <div className="h-12 w-12 rounded-2xl bg-terracotta/10 flex items-center justify-center mb-3">
              <Clock size={26} weight="duotone" className="text-terracotta" />
            </div>
            <DialogTitle className="font-display font-black text-2xl text-ink tracking-tight text-left">
              {t.common.soonPhaseTitle}
            </DialogTitle>
            <DialogDescription className="text-ink-soft font-sans leading-relaxed text-left pt-1">
              {t.common.soonPhaseBody}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              data-testid="phase-soon-close"
              onClick={() => setSoonOpen(false)}
              className="w-full sm:w-auto rounded-full bg-terracotta hover:bg-terracotta-hover text-white font-sans font-bold"
            >
              {t.common.soonPhaseCta}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
